import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { SchoolCode, SchoolInfo } from '../types';
import { SCHOOLS } from '../types';
import { useAuth } from './AuthContext';
import { waitMinDuration } from '../utils/uiLoading';
import { fetchCrossSchoolAccessStatus } from '../api/axios';

type SchoolContextValue = {
  // Current school being viewed/administered
  currentSchool: SchoolCode;
  // The user's own school (determined by their login_id)
  userSchool: SchoolCode | null;
  // Whether user is an admin (can switch schools)
  canSwitchSchool: boolean;
  /** Server session: admin may act on the other tenant (API + UI school switch). */
  crossSchoolUnlocked: boolean;
  crossSchoolExpiresAt: string | null;
  refreshCrossSchoolStatus: () => Promise<void>;
  // Function to switch schools (admin only)
  setCurrentSchool: (school: SchoolCode) => Promise<void>;
  // Indicates an in-progress school change transition
  isSwitchingSchool: boolean;
  // Monotonic identifier for the current school switch cycle
  schoolSwitchId: number;
  // Mark current school switch as complete once data is ready
  completeSchoolSwitch: (switchId: number) => void;
  // School information
  getSchoolInfo: (code: SchoolCode) => SchoolInfo;
  // CSS class for current school theming
  schoolThemeClass: string;
};

const SchoolContext = createContext<SchoolContextValue | undefined>(undefined);

export const useSchool = (): SchoolContextValue => {
  const ctx = useContext(SchoolContext);
  if (!ctx) {
    throw new Error('useSchool must be used within SchoolProvider');
  }
  return ctx;
};

// Determine school from login_id prefix
export const getSchoolFromLoginId = (loginId?: string | null): SchoolCode | null => {
  if (!loginId) return null;
  if (loginId.startsWith('TLC')) return 'TLC';
  if (loginId.startsWith('KBA')) return 'KBA';
  return null;
};

// School information lookup - using SCHOOLS from types
const SCHOOL_INFO = SCHOOLS;

export const SchoolProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const { user } = useAuth();
  
  // Determine user's school from their login_id or school field
  const userSchool = useMemo<SchoolCode | null>(() => {
    if (!user) return null;
    // First check the school field if available
    if (user.school === 'KBA' || user.school === 'TLC') return user.school;
    // Otherwise derive from login_id
    return getSchoolFromLoginId(user.login_id);
  }, [user]);
  const hasInvalidSchoolContext = Boolean(user && !userSchool);

  const [crossSchoolUnlocked, setCrossSchoolUnlocked] = useState(false);
  const [crossSchoolExpiresAt, setCrossSchoolExpiresAt] = useState<string | null>(null);

  const refreshCrossSchoolStatus = useCallback(async () => {
    if (!user || user.role !== 'admin') {
      setCrossSchoolUnlocked(false);
      setCrossSchoolExpiresAt(null);
      return;
    }
    try {
      const s = await fetchCrossSchoolAccessStatus();
      setCrossSchoolUnlocked(Boolean(s.unlocked));
      setCrossSchoolExpiresAt(s.expires_at ?? null);
    } catch {
      setCrossSchoolUnlocked(false);
      setCrossSchoolExpiresAt(null);
    }
  }, [user]);

  useEffect(() => {
    void refreshCrossSchoolStatus();
    if (!user || user.role !== 'admin') {
      return;
    }
    const id = window.setInterval(() => {
      void refreshCrossSchoolStatus();
    }, 60_000);
    return () => window.clearInterval(id);
  }, [user, refreshCrossSchoolStatus]);

  // Admins may switch UI school only after cross-school unlock (server session).
  const canSwitchSchool = (user?.role === 'admin' && crossSchoolUnlocked) || (user?.role === 'dev' && user.demo_mode === true);

  // Initialize current school from localStorage for admins, or user's school
  const [currentSchool, setCurrentSchoolState] = useState<SchoolCode>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('admin_current_school');
      if (saved === 'KBA' || saved === 'TLC') {
        return saved;
      }
    }
    return userSchool ?? 'KBA';
  });
  const [isSwitchingSchool, setIsSwitchingSchool] = useState(false);
  const [schoolSwitchId, setSchoolSwitchId] = useState(0);

  // Update current school when user changes (for non-admins, always use their school)
  useEffect(() => {
    if (user && !canSwitchSchool && userSchool) {
      setCurrentSchoolState(userSchool);
    }
  }, [user, canSwitchSchool, userSchool]);

  // If admin session is not elevated, keep UI scoped to the admin's home school.
  useEffect(() => {
    if (user?.role === 'admin' && userSchool && !crossSchoolUnlocked && currentSchool !== userSchool) {
      setCurrentSchoolState(userSchool);
      localStorage.setItem('admin_current_school', userSchool);
    }
  }, [user, userSchool, crossSchoolUnlocked, currentSchool]);

  // Set current school (with persistence for admins)
  const setCurrentSchool = useCallback(async (school: SchoolCode) => {
    if (!canSwitchSchool || school === currentSchool) return;
    const startedAt = Date.now();
    const nextSwitchId = schoolSwitchId + 1;
    setSchoolSwitchId(nextSwitchId);
    setIsSwitchingSchool(true);
    setCurrentSchoolState(school);
    localStorage.setItem('admin_current_school', school);
    await waitMinDuration(startedAt, 250);
  }, [canSwitchSchool, currentSchool, schoolSwitchId]);

  const completeSchoolSwitch = useCallback((switchId: number) => {
    setSchoolSwitchId(prev => {
      if (switchId !== prev) return prev;
      setIsSwitchingSchool(false);
      return prev;
    });
  }, []);

  // Get school info by code
  const getSchoolInfo = useCallback((code: SchoolCode): SchoolInfo => {
    const base = SCHOOL_INFO[code];
    if (!user?.demo_mode) {
      return base;
    }
    const brand = user.demo_branding?.find((row) => row.school === code);
    if (!brand) {
      return base;
    }
    return {
      ...base,
      name: brand.display_name || base.name,
      theme: {
        ...base.theme,
        logoIcon: brand.logo_url || base.theme.logoIcon,
      },
    };
  }, [user?.demo_mode, user?.demo_branding]);

  // CSS class for theming
  const schoolThemeClass = useMemo(() => {
    return `school-theme-${currentSchool.toLowerCase()}`;
  }, [currentSchool]);

  // Apply school theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('school-theme-kba', 'school-theme-tlc');
    root.classList.add(schoolThemeClass);
    
    // Set CSS custom properties for school colors
    if (currentSchool === 'KBA') {
      root.style.setProperty('--school-primary', '#1e3a5f');
      root.style.setProperty('--school-primary-light', '#3b6fa0');
      root.style.setProperty('--school-primary-dark', '#162d4a');
      root.style.setProperty('--school-accent', '#d4af37');
      root.style.setProperty('--school-accent-light', '#e8c860');
      root.style.setProperty('--school-gradient', '#1e3a5f');
    } else {
      root.style.setProperty('--school-primary', '#15803d');
      root.style.setProperty('--school-primary-light', '#22c55e');
      root.style.setProperty('--school-primary-dark', '#166534');
      root.style.setProperty('--school-accent', '#f59e0b');
      root.style.setProperty('--school-accent-light', '#fbbf24');
      root.style.setProperty('--school-gradient', '#15803d');
    }
  }, [currentSchool, schoolThemeClass]);

  const value = useMemo<SchoolContextValue>(() => ({
    currentSchool,
    userSchool,
    canSwitchSchool,
    crossSchoolUnlocked,
    crossSchoolExpiresAt,
    refreshCrossSchoolStatus,
    setCurrentSchool,
    isSwitchingSchool,
    schoolSwitchId,
    completeSchoolSwitch,
    getSchoolInfo,
    schoolThemeClass,
  }), [currentSchool, userSchool, canSwitchSchool, crossSchoolUnlocked, crossSchoolExpiresAt, refreshCrossSchoolStatus, setCurrentSchool, isSwitchingSchool, schoolSwitchId, completeSchoolSwitch, getSchoolInfo, schoolThemeClass]);

  if (hasInvalidSchoolContext) {
    return (
      <div style={{ padding: 16, color: '#b91c1c', fontWeight: 600 }}>
        School context is invalid for this account. Please sign out and sign in again.
      </div>
    );
  }

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
};

